import React from "react";
import { useSelector } from "react-redux";
import { alpha, Chip, Container, Skeleton, Typography } from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { DataGrid } from "@mui/x-data-grid";
import UsersTableActions from "../UsersTableActions/index.js";
import styles from "./usersTableStyles.js";
import { RecordCircle } from "iconsax-react";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

function StatusIcon({ status }) {
  if (status === "activo") {
    return (
      <Chip
        sx={{
          color: "#53CA98",
          backgroundColor: alpha("#53CA98", 0.1),
        }}
        icon={
          <RecordCircle size="12" variant="Bold" />
          // <Chainlink size="12" variant="Bold"/>
        }
        label="Activo"
        size="small"
      />
    );
  } else {
    return (
      <Chip
        sx={{
          color: "#DB463C",
          backgroundColor: alpha("#DB463C", 0.1),
        }}
        icon={<RecordCircle size="12" variant="Bold" />}
        label="Inactivo"
        size="small"
      />
    );
  }
}

const UsersTable = ({ users = [], search, isLoading, fetchUsers }) => {
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const { hasAnyPermission, hasPermission } = usePermissions();

  const actionPermissions = ["usr_vwd", "usr_del", "usr_chp", "usr_per_v"];

  const canViewUsers = hasPermission("usr_vw");

  const canSeeActions = canViewUsers && hasAnyPermission(actionPermissions);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 50,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>ID</strong>,
    },
    {
      field: "fullname",
      headerName: "NOMBRE",
      minWidth: 200,
      flex: 1,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>NOMBRE</strong>,
    },
    {
      field: "status",
      headerName: "ESTADO",
      width: 100,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>ESTADO</strong>,
      renderCell: (params) => <StatusIcon status={params.value} />,
    },
    {
      field: "role",
      headerName: "TIPO",
      type: "string",
      width: 110,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>TIPO</strong>,
    },
    ...(isSuperAdmin
      ? [
          {
            field: "region",
            headerName: "REGIÓN",
            type: "string",
            width: 110,
            resizable: false,
            sortable: false,
            headerAlign: "left",
            align: "left",
            renderHeader: (params) => <strong>REGIÓN</strong>,
          },
        ]
      : []),
    {
      field: "createdAt",
      headerName: "FECHA DE CREACIÓN",
      type: "string",
      width: 180,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>FECHA DE CREACIÓN</strong>,
    },
    ...(canSeeActions
      ? [
          {
            field: "actions",
            headerName: "ACCIONES",
            type: "string",
            width: 160,
            resizable: false,
            sortable: false,
            headerAlign: "left",
            align: "left",
            renderHeader: (params) => <strong>ACCIONES</strong>,
            renderCell: (params) => (
              <UsersTableActions fetchUsers={fetchUsers} user={params.row} />
            ),
          },
        ]
      : []),
  ];

  const transformStatus = (status) => (status ? "activo" : "inactivo");

  const filteredRows = users.filter((user) => {
    const transformedUser = {
      ...user,
      status: transformStatus(user.user_state),
    };
    return Object.values(transformedUser).some((value) =>
      value.toString().toLowerCase().includes(search.toLowerCase())
    );
  });

  const rows = filteredRows?.map((user, index) => {
    const regionNames =
      Array.isArray(user.available_zones_names) &&
      user.available_zones_names.length > 0
        ? user.available_zones_names.join(", ")
        : "Sin región";

    return {
    id: user.id_user,
    fullname: `${user.user_name} ${user.paternal_lastname} ${user.maternal_lastname}`,
    name: user.user_name,
    paternalLastname: user.paternal_lastname,
    maternalLastname: user.maternal_lastname,
    status: transformStatus(user.user_state),
    role: user?.role_type === "sysliber" 
      ? user.is_zone_manager === true 
        ? "Administrador Regional" 
        : "Administrador" 
      : user?.role_type,
    region: regionNames,
    email: user.email,
    isRegionalAdmin: user.is_zone_manager,
    phoneNumber: user.telephone_number,
    createdAt: user.register_date,
    permissions: user.permissions || [],
    };
  });

  return (
    <Container sx={styles.mainContainer}>
      {isLoading ? (
        <Container sx={styles.skeletonMainContainer}>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={"5%"} height={40} />
            <Skeleton variant="rounded" width={"25%"} height={40} />
            <Skeleton variant="rounded" width={"10%"} height={40} />
            <Skeleton variant="rounded" width={"10%"} height={40} />
            {isSuperAdmin && <Skeleton variant="rounded" width={"10%"} height={40} />}
            <Skeleton variant="rounded" width={"20%"} height={40} />
            <Skeleton variant="rounded" width={"25%"} height={40} />
          </Container>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={"5%"} height={300} />
            <Skeleton variant="rounded" width={"25%"} height={300} />
            <Skeleton variant="rounded" width={"10%"} height={300} />
            <Skeleton variant="rounded" width={"10%"} height={300} />
            {isSuperAdmin && <Skeleton variant="rounded" width={"10%"} height={300} />}
            <Skeleton variant="rounded" width={"20%"} height={300} />
            <Skeleton variant="rounded" width={"25%"} height={300} />
          </Container>
        </Container>
      ) : (
        <DataGrid
          autoHeight
          disableColumnFilter
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          getRowHeight={() => "auto"}
          sx={{
            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
              py: "8px",
              display: "flex",
              alignItems: "center",
            },
            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
              py: "10px",
              display: "flex",
              alignItems: "center",
            },
            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
              py: "22px",
              display: "flex",
              alignItems: "center",
            },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          disableColumnMenu={true}
          disableSelectionOnClick={true}
          localeText={{ noRowsLabel: <NoResultsComponent /> }}
        />
      )}
    </Container>
  );
};

const NoResultsComponent = () => (
  <Container sx={styles.noDataContainer}>
    <SearchOffIcon sx={styles.noDataIcon} />
    <Typography sx={styles.noDataTitle}>No se encontraron usuarios</Typography>
  </Container>
);

export default UsersTable;
